/**
 * The Runs page's pure logic: one row per QC check, catalog job or plain job (the run that steers
 * it, its step, its latest run, when it started), run states for the `?state=` control, search
 * text, verdict counts, understanding facts and the estimated writing cost.
 */
import { diffInMs, formatDurationMs } from '~/utils/date';

import type {
  GenerationRunDetail,
  GenerationRunRead,
  TokenUsageRead,
} from '../types/content.types';
import type { PhaseSummary, ScoreSummary } from '../types/quality-scores.types';
import type { ReviewStatsRead } from '../types/review-products.types';

import { COMMON_VERTICAL, NO_MATCH } from './catalog-products';
import type { CatalogJob } from './catalog-run-ids';
import { parseRunId } from './catalog-run-ids';
import type { CatalogStep } from './catalog-steps';
import {
  CATALOG_STEP_LABELS,
  catalogJobTitle,
  catalogStepInfo,
  defaultCatalogStep,
} from './catalog-steps';
import { formatCount } from './format-count';
import { GLOSSARY } from './glossary';
import type { FlowPrice } from './qc-cost';
import { costPerProduct } from './qc-cost';
import type { StatusTone } from './run-status';
import { humanizeEnum, isRunInFlight, isTerminalRunStatus } from './run-status';
import { runDuration, runPercent, runPurpose, runScopeLabel, runTitleLabel } from './run-summary';

/**
 * The kinds of row on the Runs list (`?type=` filter, and the row's type badge); `run` is a run
 * started from "Start a run" (Overview, Review, AI instructions) — neither a QC check nor a catalog run.
 */
export const RUN_TYPES = ['qc', 'catalog', 'run'] as const;
export type RunType = (typeof RUN_TYPES)[number];

/** Each kind's badge / filter label. */
export const RUN_TYPE_LABEL: Record<RunType, string> = {
  qc: 'QC check',
  catalog: 'Catalog run',
  run: 'Run',
};

/**
 * A run's name from what it ran: understanding runs say which of the two steps they did
 * (product type detection, attribute extraction, or both); other runs use `runTitleLabel`.
 */
export function runKindTitle(run: Pick<GenerationRunRead, 'fields' | 'flows'>): string {
  if (runPurpose(run) !== 'understand') return runTitleLabel(run);
  const detects = run.flows.includes('PRODUCT_TYPE_DETECTION');
  const extracts = run.flows.includes('CORE_ATTRIBUTION');
  if (detects && extracts) return GLOSSARY.typeAndAttributes.label;
  return detects ? 'Product group detection' : 'Attribute extraction';
}

/** One catalog job as the Jobs table shows it. */
export interface CatalogJobRow {
  job: CatalogJob;
  /** The run the job's title and size come from: the full run, else the sample. */
  primary: GenerationRunRead | null;
  /** The newest run of the job — its last fix, else the full run, else the sample. */
  latest: GenerationRunRead | null;
  /** The step the job page opens on; null when only fixes of the job are loaded. */
  step: CatalogStep | null;
  /** "Full run" for a job that went straight to the full run, with "Sample skipped" under it. */
  stepLabel: string;
  stepNote: string | null;
  startedAt: string | null;
}

/**
 * A catalog job's row: step from `defaultCatalogStep` (the furthest reached), a note for a
 * skipped sample, and the latest run for the status (fixes come after the full run). `hasMore`:
 * older runs aren't loaded, so a missing sample may just be on an unread page — no "skipped" note.
 */
export function catalogJobRow(job: CatalogJob, hasMore = false): CatalogJobRow {
  const primary = job.full ?? job.sample ?? null;
  const latest = job.fixes.at(-1) ?? job.full ?? job.sample ?? null;
  const step = primary ? defaultCatalogStep(job, undefined) : null;
  const info = catalogStepInfo(job);
  // A finished sample waiting for you is the status pill's "Sample ready" — not repeated here.
  const stepNote: string | null = info.sample.skipped && !hasMore ? 'Sample skipped' : null;
  return {
    job,
    primary,
    latest,
    step,
    stepLabel: step ? CATALOG_STEP_LABELS[step] : '—',
    stepNote,
    startedAt: job.sample?.createdAt ?? job.full?.createdAt ?? job.fixes[0]?.createdAt ?? null,
  };
}

/** Where a run stands, for the status control and its `?state=` filter. */
export const JOB_STATES = ['running', 'waiting', 'finished', 'failed'] as const;
export type JobState = (typeof JOB_STATES)[number];

/** Label and tone per run state, shared by the status control and the stat cards. */
export const JOB_STATE_INFO: Record<JobState, { label: string; tone: StatusTone }> = {
  running: { label: 'Running', tone: 'info' },
  waiting: { label: 'Waiting for you', tone: 'warning' },
  finished: { label: 'Finished', tone: 'success' },
  // Grey, not red: most are cancelled on purpose, and the status pill says which.
  failed: { label: 'Failed / Cancelled', tone: 'muted' },
};

/**
 * A run's state: paused = waiting for you (only a resume moves it on); an unknown status counts
 * as running, like the rest of the section, so a newer worker's status never reads as finished.
 */
export function runJobState(run: Pick<GenerationRunRead, 'status'>): JobState {
  if (run.status === 'PAUSED') return 'waiting';
  if (run.status === 'COMPLETED') return 'finished';
  if (run.status === 'FAILED' || run.status === 'CANCELLED') return 'failed';
  return 'running';
}

/**
 * A catalog job's state from its latest run; a finished sample with nothing after it also waits
 * for you (continue, change the setup or stop).
 */
export function catalogJobState(row: CatalogJobRow): JobState {
  const { job } = row;
  if (job.sample?.status === 'COMPLETED' && !job.full && job.fixes.length === 0) return 'waiting';
  return row.latest ? runJobState(row.latest) : 'finished';
}

/** How many jobs sit in each state. */
export function countJobStates<T>(
  items: readonly T[],
  stateOf: (item: T) => JobState,
): Record<JobState, number> {
  const counts: Record<JobState, number> = { running: 0, waiting: 0, finished: 0, failed: 0 };
  for (const item of items) counts[stateOf(item)] += 1;
  return counts;
}

/** How many products a finished run wrote (enriched + partial) out of its total. */
export interface WrittenCount {
  written: number;
  total: number;
}

/** The phase a run's verdict counts come from: after, or the feed for a run that only scored. */
export function verdictPhase(
  summary: Pick<ScoreSummary, 'pre' | 'post'> | null | undefined,
  writes: boolean,
): PhaseSummary | null {
  if (!summary) return null;
  return summary.post ?? (writes ? null : summary.pre);
}

/**
 * Estimated writing cost from a run's token totals at a model price, or null when the price is
 * not real yet (placeholder pricing) — the tokens are still worth showing then.
 */
export function writingCost(
  tokens: Pick<TokenUsageRead, 'promptTokens' | 'completionTokens'>,
  price: FlowPrice | null,
): number | null {
  return costPerProduct(
    { prompt: tokens.promptTokens, completion: tokens.completionTokens },
    price,
  );
}

/** How often the Jobs list re-reads its loaded pages while a run moves. */
const JOBS_POLL_MS = 10_000;

/**
 * A run counts as moving for polling only if it changed recently — a run stuck in a running
 * status for hours (a lost worker) must not keep the list polling forever.
 */
const JOBS_POLL_RECENT_MS = 2 * 60 * 60 * 1000;

/**
 * The Jobs list's poll: every {@link JOBS_POLL_MS} while any loaded run is in flight and was
 * updated recently, else off. `now` is an ISO instant (from `nowISO()`).
 */
export function jobsPollInterval(runs: readonly GenerationRunRead[], now: string): number | false {
  const moving = runs.some((run) => {
    if (!isRunInFlight(run.status)) return false;
    const touched = run.updatedAt ?? run.createdAt;
    return !touched || diffInMs(touched, now) < JOBS_POLL_RECENT_MS;
  });
  return moving ? JOBS_POLL_MS : false;
}

/** The run stages (`RunStage`) in words, as the run pages' phase stepper names them. */
const STAGE_LABEL: Record<string, string> = {
  QUEUED: 'Queued',
  PLANNING: 'Getting products',
  FANNING_OUT: 'Splitting into batches',
  AWAITING_BATCHES: 'Working through batches',
  PAUSED: 'Paused',
  FINALIZING: 'Saving results',
  DONE: 'Finished',
};

/** A stage code in words; an unknown (newer) code is humanised, a blank one is "". */
export function stageLabel(stage: string): string {
  if (!stage) return '';
  return STAGE_LABEL[stage] ?? humanizeEnum(stage);
}

/** "~2 min left" / "~1h 20m left" / "under a minute left", or "" when the ETA is unknown. */
function etaLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return '';
  }
  if (seconds < 60) return 'under a minute left';
  if (seconds < 3600) return `~${String(Math.round(seconds / 60))} min left`;
  return `~${formatDurationMs(Math.round(seconds / 60) * 60_000)} left`;
}

/** A moving run's status line and progress for the Runs table. */
export interface LiveRunLine {
  /** "Awaiting batches · 11 of 25 · ~2 min left". */
  text: string;
  /** 0–100 for the bar. */
  percent: number;
  /** "11 of 25 products done", for the bar's label. */
  barLabel: string;
}

/**
 * A moving (or paused) run's line from its live detail (`GET /runs/{id}` progress): stage, products
 * done of total (exact from finished batches; batches when the engine hasn't counted products yet)
 * and the ETA. Falls back to the list row's stored counters until the detail lands.
 */
export function liveRunLine(
  run: GenerationRunRead,
  detail: GenerationRunDetail | null | undefined,
): LiveRunLine {
  const progress = detail?.progress ?? null;
  // The live stage / total when the workflow answered; the stored ones when it's blank or zero.
  const liveStage = progress?.stage ?? '';
  const stage =
    run.status === 'PAUSED' ? 'Paused' : stageLabel(liveStage === '' ? run.stage : liveStage);
  const liveTotal = progress?.totalProducts ?? 0;
  const total = liveTotal > 0 ? liveTotal : run.totalProducts;
  const done = progress?.productsDone ?? null;
  const parts: string[] = stage ? [stage] : [];
  let percent: number;
  let barLabel: string;
  if (done !== null && total > 0) {
    parts.push(`${formatCount(done)} of ${formatCount(total)}`);
    percent = Math.min(100, Math.round((done / total) * 100));
    barLabel = `${formatCount(done)} of ${formatCount(total)} products done`;
  } else {
    const counters = progress ?? run;
    if (counters.totalBatches > 0) {
      parts.push(
        `batch ${formatCount(counters.completedBatches)} of ${formatCount(counters.totalBatches)}`,
      );
    }
    percent = detail ? runPercent(detail) : 0;
    barLabel = `${String(percent)}% done`;
  }
  if (run.status !== 'PAUSED') {
    const eta = etaLabel(progress?.etaSeconds);
    if (eta) parts.push(eta);
  }
  return { text: parts.join(' · '), percent, barLabel };
}

/**
 * A finished run's line: "Took 3m 50s" (QC), "172 of 211 written · 18m 40s" (catalog, or any
 * cancelled / failed run that wrote) — `written` is null while its counts load or when not read.
 */
export function finishedRunLine(
  run: GenerationRunRead,
  written: WrittenCount | null,
  withWritten: boolean,
): string {
  const took = runDuration(run);
  if (withWritten && written && written.total > 0) {
    const count = `${formatCount(written.written)} of ${formatCount(written.total)} written`;
    return run.status === 'COMPLETED' && took ? `${count} · ${took}` : count;
  }
  if (run.status === 'COMPLETED' && took) return `Took ${took}`;
  return '';
}

/** Whether a run is still moving or paused — its row shows live progress, not a final line. */
export function isRunLive(run: Pick<GenerationRunRead, 'status'>): boolean {
  return !isTerminalRunStatus(run.status);
}

/** One row of the Runs list: a QC check, a catalog job (sample, full run, fixes), or a plain job. */
export interface RunsListRow {
  type: RunType;
  /** The QC / job run id, or the catalog job id — unique across the list. */
  key: string;
  /** The run the title, size, score, verdicts and channel come from (catalog: full, else sample). */
  main: GenerationRunRead | null;
  /** The newest run, whose status the row shows (catalog: the last fix, else full, else sample). */
  latest: GenerationRunRead | null;
  /** The catalog job's row (step, notes); null for a QC check. */
  catalog: CatalogJobRow | null;
  state: JobState;
  startedAt: string | null;
  /** Every run id in the row — what a search by id matches. */
  runIds: string[];
}

/** Every run of a catalog job, oldest step first. */
export function catalogJobRuns(row: CatalogJobRow): GenerationRunRead[] {
  return [row.job.sample, row.job.full, ...row.job.fixes].filter(
    (run): run is GenerationRunRead => !!run,
  );
}

/** A single-run row (a QC check or a plain job): the run is its own main and latest run. */
function singleRunRow(type: 'qc' | 'run', run: GenerationRunRead): RunsListRow {
  return {
    type,
    key: run.runId,
    main: run,
    latest: run,
    catalog: null,
    state: runJobState(run),
    startedAt: run.createdAt,
    runIds: [run.runId],
  };
}

/**
 * QC checks, catalog jobs and plain jobs (`others` — runs from "Start a run") as one list, newest
 * start first — the Runs table's rows, so every run the service returned is reachable.
 */
export function runsListRows(
  qcChecks: readonly GenerationRunRead[],
  catalogRows: readonly CatalogJobRow[],
  others: readonly GenerationRunRead[] = [],
): RunsListRow[] {
  const rows: RunsListRow[] = [
    ...qcChecks.map((run) => singleRunRow('qc', run)),
    ...others.map((run) => singleRunRow('run', run)),
    ...catalogRows.map(
      (row): RunsListRow => ({
        type: 'catalog',
        key: row.job.jobId,
        main: row.primary,
        latest: row.latest,
        catalog: row,
        state: catalogJobState(row),
        startedAt: row.startedAt,
        runIds: [row.job.jobId, ...catalogJobRuns(row).map((run) => run.runId)],
      }),
    ),
  ];
  // ISO instants sort as strings; a row with no start goes last.
  return rows.sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''));
}

/** Whether a row matches the search box: its title, who started it, or any of its run ids. */
export function runRowMatches(row: RunsListRow, title: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [title, row.main?.requestedBy ?? '', ...row.runIds].join(' ').toLowerCase();
  return haystack.includes(needle);
}

/** What a catalog run is inside its job, as the Review list names it. */
const CATALOG_KIND_LABEL = { sample: 'Sample', full: 'Full run', fix: 'Fix' } as const;

/**
 * One run's title outside the Runs table (the Review list): QC / plain jobs by what they ran and
 * their scope, catalog runs by their setup and which run of the job they are.
 */
export function runListTitle(run: GenerationRunRead): string {
  const parsed = parseRunId(run.runId);
  if (parsed.kind === 'qc' || parsed.kind === 'other') {
    return `${runKindTitle(run)} · ${runScopeLabel(run)}`;
  }
  const kind =
    parsed.kind === 'fix'
      ? `${CATALOG_KIND_LABEL.fix} ${String(parsed.fixN ?? '')}`.trim()
      : CATALOG_KIND_LABEL[parsed.kind];
  return `${catalogJobTitle(run, undefined)} · ${kind}`;
}

/** Which list type a single run belongs to, for its badge outside the Runs table. */
export function runRowType(run: Pick<GenerationRunRead, 'runId'>): RunType {
  const { kind } = parseRunId(run.runId);
  if (kind === 'qc') return 'qc';
  return kind === 'other' ? 'run' : 'catalog';
}

/** An understanding run's headline facts from its `/review-stats` (no product reads). */
export interface UnderstandingFacts {
  reviewed: number;
  /** Products with rows to review. */
  products: number;
  /** Distinct product types found (No match / Common left out); null when the run didn't detect. */
  typesFound: number | null;
  /** Share of products with no matching type, 0–100; null when the run didn't detect. */
  noMatchPercent: number | null;
  /** Share of asked details the AI answered, 0–100; null when the run didn't extract. */
  answeredPercent: number | null;
  /** Products carrying at least one high-severity QC flag. */
  highFlagged: number;
}

/**
 * The facts an understanding row shows instead of a score: reviewed of total, types found and the
 * no-match share (detection), details answered (extraction) and products with high QC flags.
 */
export function understandingFacts(
  run: Pick<GenerationRunRead, 'flows'>,
  stats: ReviewStatsRead,
): UnderstandingFacts {
  // A writing run detects and extracts on the way, so an empty flow list counts as both.
  const detects = run.flows.length === 0 || run.flows.includes('PRODUCT_TYPE_DETECTION');
  const extracts = run.flows.length === 0 || run.flows.includes('CORE_ATTRIBUTION');
  const noMatch = stats.verticals.find((facet) => facet.value === NO_MATCH)?.products ?? 0;
  const typesFound = stats.verticals.filter(
    (facet) => facet.value !== '' && facet.value !== NO_MATCH && facet.value !== COMMON_VERTICAL,
  ).length;
  return {
    reviewed: stats.reviewStatus.reviewed,
    products: stats.products,
    typesFound: detects ? typesFound : null,
    noMatchPercent:
      detects && stats.products > 0 ? Math.round((noMatch / stats.products) * 100) : null,
    answeredPercent:
      extracts && stats.sectionsAsked > 0
        ? Math.round((stats.sectionsAnswered / stats.sectionsAsked) * 100)
        : null,
    highFlagged: stats.productsWithQc.high,
  };
}
